import { Injectable } from '@angular/core';
import type { FilterService } from '../shared/filter.service';
import type { SearchService } from '../shared/search.service';
import type { UiStateService } from '../shared/ui-state.service';
import type { ScrollPositionService } from '../shared/scroll-position.service';

export const AUDIT_LIST_SNAPSHOT_KEY = 'dgag_audit_list_snapshot';
export const AUDIT_EXPECT_RESTORE_KEY = 'dgag_audit_expect_restore';

/** Desktop rows-per-page options - keep in sync with passports desktop table */
const AUDIT_DESKTOP_SHOW_ROWS_OPTIONS = new Set([10, 25, 50, 100, 150, 200, 250]);

const AUDIT_SNAPSHOT_STRING_KEYS = [
  'filterScheduledDate',
  'filterLoadNumber',
  'filterVendorNumber',
  'filterDestination',
  'filterVariety',
] as const;

export function normalizeAuditDesktopShowRows(size: number): number {
  if (AUDIT_DESKTOP_SHOW_ROWS_OPTIONS.has(size)) {
    return size;
  }
  return 10;
}

export interface AuditListSnapshot {
  filterScheduledDate: string;
  filterLoadNumber: string;
  /** Serialized crop year; restored as string or number for `[(ngModel)]` */
  filterCropYear: string | number;
  filterVendorNumber: string;
  filterDestination: string;
  filterVariety: string;
  hasSearched: boolean;
  filtersCollapsed: boolean;
  filters: Record<string, string[]>;
  searchQuery: string;
  currentPage: number;
  itemsPerPage: number;
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
  passportId: string;
  scrollY?: number;
}

export interface LeaveAuditPersonaListStateDeps {
  filterService: FilterService;
  searchService: SearchService;
  ui: UiStateService;
  scrollPosition: ScrollPositionService;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'string');
}

function isFiltersRecord(value: unknown): value is Record<string, string[]> {
  if (!isRecord(value)) {
    return false;
  }
  for (const v of Object.values(value)) {
    if (!isStringArray(v)) {
      return false;
    }
  }
  return true;
}

function hasRequiredAuditSnapshotStringFields(value: Record<string, unknown>): boolean {
  for (const key of AUDIT_SNAPSHOT_STRING_KEYS) {
    if (typeof value[key] !== 'string') {
      return false;
    }
  }
  return true;
}

function isStringOrNumber(value: unknown): boolean {
  return typeof value === 'string' || typeof value === 'number';
}

function isFinitePageNumber(value: unknown): boolean {
  return typeof value === 'number' && Number.isFinite(value);
}

function isSortDirectionPair(value: unknown): value is 'asc' | 'desc' {
  return value === 'asc' || value === 'desc';
}

function isOptionalFiniteScrollY(value: unknown): boolean {
  if (value === undefined) {
    return true;
  }
  return typeof value === 'number' && Number.isFinite(value);
}

function isAuditListSnapshot(value: unknown): value is AuditListSnapshot {
  if (!isRecord(value)) {
    return false;
  }
  if (!hasRequiredAuditSnapshotStringFields(value)) {
    return false;
  }
  const crop = value['filterCropYear'];
  if (!isStringOrNumber(crop)) {
    return false;
  }
  if (typeof value['hasSearched'] !== 'boolean') {
    return false;
  }
  if (typeof value['filtersCollapsed'] !== 'boolean') {
    return false;
  }
  if (!isFiltersRecord(value['filters'])) {
    return false;
  }
  if (typeof value['searchQuery'] !== 'string') {
    return false;
  }
  if (!isFinitePageNumber(value['currentPage'])) {
    return false;
  }
  if (!isFinitePageNumber(value['itemsPerPage'])) {
    return false;
  }
  if (typeof value['sortColumn'] !== 'string') {
    return false;
  }
  if (!isSortDirectionPair(value['sortDirection'])) {
    return false;
  }
  if (typeof value['passportId'] !== 'string') {
    return false;
  }
  if (!isOptionalFiniteScrollY(value['scrollY'])) {
    return false;
  }
  return true;
}

export function peekAuditExpectRestore(): boolean {
  try {
    return globalThis.sessionStorage?.getItem(AUDIT_EXPECT_RESTORE_KEY) === '1';
  } catch {
    return false;
  }
}

export function hasAuditListSnapshot(): boolean {
  try {
    const raw = globalThis.sessionStorage?.getItem(AUDIT_LIST_SNAPSHOT_KEY);
    return typeof raw === 'string' && raw.length > 0;
  } catch {
    return false;
  }
}

export function clearAuditListRestoreState(): void {
  try {
    globalThis.sessionStorage?.removeItem(AUDIT_LIST_SNAPSHOT_KEY);
    globalThis.sessionStorage?.removeItem(AUDIT_EXPECT_RESTORE_KEY);
  } catch {
    // sessionStorage may be unavailable (SSR / private mode)
  }
}

export function saveAuditListSnapshot(snapshot: AuditListSnapshot): void {
  try {
    globalThis.sessionStorage?.setItem(AUDIT_LIST_SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch {
    // ignore
  }
}

export function parseAuditListSnapshot(): AuditListSnapshot | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(AUDIT_LIST_SNAPSHOT_KEY);
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!isAuditListSnapshot(parsed)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function leaveAuditPersonaListState(deps: LeaveAuditPersonaListStateDeps): void {
  clearAuditListRestoreState();
  deps.filterService.clear();
  deps.searchService.clear();
  deps.ui.setSearchOpen(false);
  deps.ui.setFiltersOpen(false);
  deps.ui.setSchedulerTabSelected('all');
  deps.scrollPosition.clearScrollPosition('/audit');
}

/**
 * Lets AuditPassportCellComponent persist list snapshot before navigating to passport details.
 */
@Injectable({
  providedIn: 'root'
})
export class AuditListSnapshotBridgeService {
  private saveHandler: ((passportId: string) => void) | null = null;

  registerSaveHandler(handler: (passportId: string) => void): void {
    this.saveHandler = handler;
  }

  unregister(): void {
    this.saveHandler = null;
  }

  beforeNavigateToPassportDetails(passportId: string): void {
    const handler = this.saveHandler;
    if (handler) {
      handler(passportId);
    }
  }
}
